import { useMemo, useState, type ReactElement, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { TabBar } from "@/components/ui/TabBar";
import { ToastContainer } from "@/components/ui/Toast";
import { useToast } from "@/hooks/useToast";
import { strategiesApi } from "@/lib/api";
import type { Strategy, StrategyWithToken } from "@/types/strategy";
import { CreateStrategyDialog } from "./CreateStrategyDialog";
import { readStoredHorizonTab, storeHorizonTab, type HorizonTab } from "./horizonTabs";
import { StrategyCard } from "./StrategyCard";
import { TokenRevealDialog } from "./TokenRevealDialog";
import { STRATEGIES_KEY, useStrategyActions, type TokenReveal } from "./useStrategyActions";

type Filter = "all" | "active" | "disabled";

interface CreateReveal extends TokenReveal {
  /** Always false since bias retired (2026-08-28) — every surviving
   * strategy generates its own signals and shows an info note instead of
   * webhook URLs. Kept so the dialog's two shapes stay explicit. */
  webhook: boolean;
  /** Which internal engine the note names — only read when `webhook` is
   * false. */
  engineLabel?: string;
  /** The engine's market-data venue named in the note (Fyers for
   * supertrend/ut_bot, Zerodha for bb_bottom_buy). Only read when
   * `webhook` is false. */
  engineSource?: string;
  entryUrl?: string;
  exitUrl?: string;
}

interface PageState {
  createOpen: boolean;
  reveal: CreateReveal | null;
  search: string;
  filter: Filter;
}

function usePageState(): {
  s: PageState;
  setCreateOpen: (v: boolean) => void;
  setReveal: (v: CreateReveal | null) => void;
  setSearch: (v: string) => void;
  setFilter: (v: Filter) => void;
} {
  const [createOpen, setCreateOpen] = useState(false);
  const [reveal, setReveal] = useState<CreateReveal | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  return {
    s: { createOpen, reveal, search, filter },
    setCreateOpen,
    setReveal,
    setSearch,
    setFilter,
  };
}

export function StrategiesPage(): ReactElement {
  const toast = useToast();
  const page = usePageState();
  const [horizonTab, setHorizonTab] = useState<HorizonTab>(readStoredHorizonTab);
  const list = useQuery({ queryKey: STRATEGIES_KEY, queryFn: () => strategiesApi.list() });
  const actions = useStrategyActions(toast, {
    onCreated: (s) => {
      page.setCreateOpen(false);
      page.setReveal(makeCreateReveal(s));
    },
  });
  const filtered = useMemo(
    () => filterStrategies(list.data ?? [], page.s.search, page.s.filter),
    [list.data, page.s.search, page.s.filter],
  );

  return (
    <main className="mx-auto max-w-6xl px-6 py-6">
      <Header
        count={list.data?.length ?? 0}
        onNew={() => {
          page.setCreateOpen(true);
        }}
      />
      <Filters
        search={page.s.search}
        onSearchChange={page.setSearch}
        filter={page.s.filter}
        onFilterChange={page.setFilter}
      />
      <StrategyList
        loading={list.isPending}
        error={list.error}
        strategies={filtered}
        tab={horizonTab}
        onTabChange={(next) => {
          setHorizonTab(next);
          storeHorizonTab(next);
        }}
      />
      <PageDialogs page={page} actions={actions} />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </main>
  );
}

interface PageDialogsProps {
  page: ReturnType<typeof usePageState>;
  actions: ReturnType<typeof useStrategyActions>;
}

function PageDialogs({ page, actions }: Readonly<PageDialogsProps>): ReactElement {
  return (
    <>
      <CreateStrategyDialog
        open={page.s.createOpen}
        busy={actions.isPending}
        onCancel={() => {
          page.setCreateOpen(false);
        }}
        onSubmit={actions.create}
      />
      <RevealDialog
        reveal={page.s.reveal}
        onClose={() => {
          page.setReveal(null);
        }}
      />
    </>
  );
}

interface RevealDialogProps {
  reveal: CreateReveal | null;
  onClose: () => void;
}

interface RevealMain {
  open: boolean;
  token: string | null;
  title: string;
  description: string;
  webhook: boolean;
  engineLabel?: string;
  engineSource?: string;
}

interface RevealOptional {
  entryUrl?: string;
  exitUrl?: string;
}

function mainRevealFields(reveal: CreateReveal | null): RevealMain {
  return {
    open: reveal !== null,
    token: reveal?.token ?? null,
    title: reveal?.title ?? "",
    description: reveal?.description ?? "",
    webhook: reveal?.webhook ?? true,
    ...engineLabelField(reveal),
    ...engineSourceField(reveal),
  };
}

function engineLabelField(reveal: CreateReveal | null): Pick<RevealMain, "engineLabel"> {
  return reveal?.engineLabel !== undefined ? { engineLabel: reveal.engineLabel } : {};
}

function engineSourceField(reveal: CreateReveal | null): Pick<RevealMain, "engineSource"> {
  return reveal?.engineSource !== undefined ? { engineSource: reveal.engineSource } : {};
}

function optionalRevealFields(reveal: CreateReveal | null): RevealOptional {
  if (reveal === null) return {};
  return {
    ...(reveal.entryUrl !== undefined ? { entryUrl: reveal.entryUrl } : {}),
    ...(reveal.exitUrl !== undefined ? { exitUrl: reveal.exitUrl } : {}),
  };
}

function RevealDialog({ reveal, onClose }: Readonly<RevealDialogProps>): ReactElement {
  return (
    <TokenRevealDialog
      {...mainRevealFields(reveal)}
      {...optionalRevealFields(reveal)}
      onClose={onClose}
    />
  );
}

function headerSubtitle(count: number): string {
  if (count === 0) return "No strategies yet - create your first to get a webhook URL.";
  const noun = count === 1 ? "strategy" : "strategies";
  return `${String(count)} ${noun} - click a card to manage`;
}

function Header({ count, onNew }: Readonly<{ count: number; onNew: () => void }>): ReactElement {
  return (
    <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="mb-1">Strategies</h1>
        <p className="text-sm text-ink-secondary">{headerSubtitle(count)}</p>
      </div>
      <button type="button" className="btn btn-primary" onClick={onNew}>
        <i className="ti ti-plus text-base" />
        New strategy
      </button>
    </header>
  );
}

interface FiltersProps {
  search: string;
  onSearchChange: (next: string) => void;
  filter: Filter;
  onFilterChange: (next: Filter) => void;
}

function Filters({
  search,
  onSearchChange,
  filter,
  onFilterChange,
}: Readonly<FiltersProps>): ReactElement {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <div className="relative min-w-[220px] flex-1">
        <i className="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-base text-ink-muted" />
        <input
          type="search"
          className="input pl-9"
          value={search}
          onChange={(e) => {
            onSearchChange(e.target.value);
          }}
          placeholder="Search strategies..."
          aria-label="Search strategies"
        />
      </div>
      <div className="flex gap-1.5">
        <FilterChip current={filter} value="all" onSelect={onFilterChange}>
          All
        </FilterChip>
        <FilterChip current={filter} value="active" onSelect={onFilterChange}>
          Active
        </FilterChip>
        <FilterChip current={filter} value="disabled" onSelect={onFilterChange}>
          Disabled
        </FilterChip>
      </div>
    </div>
  );
}

interface FilterChipProps {
  current: Filter;
  value: Filter;
  onSelect: (v: Filter) => void;
  children: ReactNode;
}

function FilterChip({
  current,
  value,
  onSelect,
  children,
}: Readonly<FilterChipProps>): ReactElement {
  const active = current === value;
  const cls = active
    ? "pill bg-accent-bg text-accent"
    : "pill border border-line-strong bg-transparent text-ink-secondary hover:bg-surface-2";
  return (
    <button
      type="button"
      className={cls}
      onClick={() => {
        onSelect(value);
      }}
    >
      {children}
    </button>
  );
}

interface StrategyListProps {
  loading: boolean;
  error: unknown;
  strategies: Strategy[];
  tab: HorizonTab;
  onTabChange: (tab: HorizonTab) => void;
}

function StrategyList({
  loading,
  error,
  strategies,
  tab,
  onTabChange,
}: Readonly<StrategyListProps>): ReactElement {
  if (loading) {
    return (
      <p className="text-sm text-ink-secondary">
        <i className="ti ti-loader-2 animate-spin text-base" /> Loading strategies...
      </p>
    );
  }
  if (error !== null) {
    return <p className="text-sm text-danger">Failed to load strategies.</p>;
  }
  if (strategies.length === 0) return <EmptyState />;
  const positional = strategies.filter((s) => s.horizon === "POSITIONAL");
  // Intraday-only setups keep the original flat grid — the tab bar only
  // appears once a positional strategy exists.
  if (positional.length === 0) return <CardGrid strategies={strategies} />;
  const intraday = strategies.filter((s) => s.horizon !== "POSITIONAL");
  // Never land on an empty tab — when nothing intraday matches (e.g. a
  // search hit only positional strategies), show the positional side.
  const effective: HorizonTab = tab === "intraday" && intraday.length === 0 ? "positional" : tab;
  return (
    <div>
      <TabBar
        tabs={[
          { id: "intraday", label: "Intraday", icon: "ti-sun-high", count: intraday.length },
          {
            id: "positional",
            label: "Positional",
            icon: "ti-calendar-week",
            count: positional.length,
          },
        ]}
        value={effective}
        onChange={onTabChange}
      />
      <CardGrid strategies={effective === "positional" ? positional : intraday} />
    </div>
  );
}

function CardGrid({ strategies }: Readonly<{ strategies: Strategy[] }>): ReactElement {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {strategies.map((s) => (
        <StrategyCard key={s.id} strategy={s} />
      ))}
    </div>
  );
}

function EmptyState(): ReactElement {
  return (
    <div className="rounded-xl border border-dashed border-line-strong bg-surface-1 p-10 text-center">
      <i className="ti ti-target text-4xl text-ink-muted" />
      <p className="mt-3 text-sm text-ink-secondary">
        No strategies match. Try changing the filter or creating a new one.
      </p>
    </div>
  );
}

function filterStrategies(items: Strategy[], search: string, filter: Filter): Strategy[] {
  const q = search.trim().toLowerCase();
  return items.filter((s) => {
    if (q.length > 0 && !s.name.toLowerCase().includes(q) && !s.id.includes(q)) return false;
    if (filter === "active" && s.kill_switch) return false;
    if (filter === "disabled" && !s.kill_switch) return false;
    return true;
  });
}

/** STPIVOT: one strategy per index, Dhan, PAPER first (docs/stpivot.md). */
function stpivotReveal(s: StrategyWithToken): CreateReveal {
  const index = s.strategy_type === "stpivot_nifty" ? "NIFTY" : "SENSEX";
  return {
    token: s.access_token,
    webhook: false,
    engineLabel: `STPIVOT ${index}`,
    engineSource: "Dhan",
    title: `Strategy created - ${s.name}`,
    description: `STPIVOT ${index} reads the 5-minute candles from Dhan and trades through Dhan, in PAPER first — no webhook or TradingView alert to set up. SuperTrend (7,3) with the previous session's pivot R1 and S1; any number of trades a day on every weekday. Dhan must be connected on the Broker page.`,
  };
}

/** Manual trading: no signals of its own, one strategy per broker (docs/manual_trading.md). */
function manualReveal(s: StrategyWithToken): CreateReveal {
  const venue = s.broker === "fyers" ? "Fyers" : "Zerodha";
  return {
    token: s.access_token,
    webhook: false,
    engineLabel: "Manual trading",
    engineSource: venue,
    title: `Strategy created - ${s.name}`,
    description: `Manual trading has no signals of its own: it trades only when the chart sends an entry or an exit, on ${venue}, in PAPER first. The chart picks this strategy by its broker, so keep exactly one Manual trading strategy per broker. Lots come from the chart; no webhook or TradingView alert to set up.`,
  };
}

function makeCreateReveal(s: StrategyWithToken): CreateReveal {
  if (s.strategy_type === "ema_trend") {
    return {
      token: s.access_token,
      webhook: false,
      engineLabel: "5/50 EMA Trend",
      // Zerodha, deliberately: this engine READS Kite index candles but
      // PLACES its orders on Fyers, so both logins have to be current. Naming
      // only the order venue here would hide half of what it needs.
      engineSource: "Zerodha",
      title: `Strategy created - ${s.name}`,
      description:
        "5/50 EMA Trend generates its own signals from Zerodha index data and places its orders on Fyers — no webhook or TradingView alert to set up. Both logins must be current for it to trade.",
    };
  }
  if (s.strategy_type === "poi1") {
    return {
      token: s.access_token,
      webhook: false,
      engineLabel: "POI-1",
      engineSource: "Zerodha",
      title: `Strategy created - ${s.name}`,
      description:
        "POI-1 reads the opening candle from Zerodha index data and places its orders on Zerodha — no webhook or TradingView alert to set up. One trade a day: Thursday SENSEX, every other weekday NIFTY.",
    };
  }
  if (s.strategy_type === "failed_breakout") {
    return {
      token: s.access_token,
      webhook: false,
      engineLabel: "Failed Breakout",
      engineSource: "Dhan",
      title: `Strategy created - ${s.name}`,
      description:
        "Failed Breakout reads the first 15-minute candle and the 5-minute candles from Dhan and trades through Dhan, in PAPER first — no webhook or TradingView alert to set up. One trade a day: NIFTY Monday, Tuesday and Friday, SENSEX Wednesday and Thursday. Dhan must be connected on the Broker page.",
    };
  }
  if (s.strategy_type === "stpivot_nifty" || s.strategy_type === "stpivot_sensex") {
    return stpivotReveal(s);
  }
  if (s.strategy_type === "manual") return manualReveal(s);
  // Only reachable for a retired type (bias, bb_bottom_buy), which the
  // create dialog no longer offers.
  return {
    token: s.access_token,
    webhook: false,
    engineLabel: "Retired",
    engineSource: "Zerodha",
    title: `Strategy created - ${s.name}`,
    description: "This strategy type is retired — no engine will run it.",
  };
}
